import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import {
  LexicalEditor,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  BaseSelection,
  RangeSelection,
} from "lexical";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  $getSelectionStyleValueForProperty,
  $patchStyleText,
  $wrapNodes,
  $isAtNodeEnd,
} from "@lexical/selection";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
} from "@lexical/list";
import { createPortal } from "react-dom";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
} from "@lexical/rich-text";
import ColorPicker from "./ColorPicker";
import { INSERT_TABLE_COMMAND } from "@lexical/table";
import TextInput from "./TextInput";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  AddLink,
  Cancel,
  CheckCircle,
  Edit,
  FormatAlignCenter,
  FormatAlignJustify,
  FormatAlignLeft,
  FormatAlignRight,
  Redo,
  Undo,
} from "@mui/icons-material";

const LowPriority = 1;

const supportedBlockTypes = new Set([
  "paragraph",
  "quote",
  "h1",
  "h2",
  "ul",
  "ol",
]);

type BlockTypeNameMap = {
  h1: string;
  h2: string;
  h3: string;
  h4: string;
  h5: string;
  ol: string;
  paragraph: string;
  quote: string;
  ul: string;
};

const blockTypeToBlockName: BlockTypeNameMap = {
  h1: "Large Heading",
  h2: "Small Heading",
  h3: "Heading",
  h4: "Heading",
  h5: "Heading",
  ol: "Numbered List",
  paragraph: "Normal",
  quote: "Quote",
  ul: "Bulleted List",
};

const Divider = () => {
  return <div className="divider" />;
};

function positionEditorElement(editor: HTMLDivElement, rect: DOMRect | null) {
  if (rect === null) {
    editor.style.opacity = "0";
    editor.style.top = "-1000px";
    editor.style.left = "-1000px";
  } else {
    editor.style.opacity = "1";
    editor.style.top = `${rect.top + rect.height + window.pageYOffset + 10}px`;
    editor.style.left = `${
      rect.left + window.pageXOffset - editor.offsetWidth / 2 + rect.width / 2
    }px`;
  }
}

const FloatingLinkEditor = (props: { editor: LexicalEditor }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mouseDownRef = useRef(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [isEditMode, setEditMode] = useState(false);
  const [lastSelection, setLastSelection] = useState<null | BaseSelection>(
    null
  );
  const [prevLink, setPrevLink] = useState("");

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl("");
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (editorElem === null || !nativeSelection) {
      return;
    }

    const rootElement = props.editor.getRootElement();
    if (
      selection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const domRange = nativeSelection.getRangeAt(0);
      let rect;
      if (nativeSelection.anchorNode === rootElement) {
        let inner: Element;
        inner = rootElement;
        while (inner.firstElementChild != null) {
          inner = inner.firstElementChild;
        }
        rect = inner.getBoundingClientRect();
      } else {
        rect = domRange.getBoundingClientRect();
      }

      if (!mouseDownRef.current) {
        positionEditorElement(editorElem, rect);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== "link-input") {
      positionEditorElement(editorElem, null);
      setLastSelection(null);
      setEditMode(false);
      setLinkUrl("");
    }

    return true;
  }, [props.editor]);

  useEffect(() => {
    return mergeRegister(
      props.editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),

      props.editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return true;
        },
        LowPriority
      )
    );
  }, [props.editor, updateLinkEditor]);

  useEffect(() => {
    props.editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [props.editor, updateLinkEditor]);

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditMode]);

  return (
    <div ref={editorRef} className="link-editor">
      {isEditMode ? (
        <div className="link-input">
          <input
            ref={inputRef}
            value={linkUrl}
            onChange={(event) => {
              setLinkUrl(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                if (lastSelection !== null) {
                  if (linkUrl !== "") {
                    props.editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
                  }
                  setEditMode(false);
                }
              } else if (event.key === "Escape") {
                event.preventDefault();
                setEditMode(false);
              }
            }}
          />
          <div className="dialog-buttons">
            <button
              type="button"
              className="email-style-button"
              onClick={() => {
                setLinkUrl(prevLink);
                setEditMode(false);
              }}
            >
              <Cancel />
            </button>
            <button
              type="button"
              className="email-style-button"
              onClick={() => {
                if (lastSelection !== null) {
                  if (linkUrl !== "") {
                    props.editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
                  }
                }
                setEditMode(false);
              }}
            >
              <CheckCircle />
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="link-input">
            <a href={linkUrl} target="_blank" rel="noopener noreferrer">
              {linkUrl}
            </a>
            <button
              type="button"
              className="email-style-button"
              tabIndex={0}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setEditMode(true);
                setPrevLink(linkUrl);
              }}
            >
              <Edit />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const getSelectedNode = (selection: RangeSelection) => {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
};

const BlockOptionsDropdownList = (props: {
  editor: LexicalEditor;
  blockType: string;
  toolbarRef: RefObject<HTMLDivElement>;
  setShowBlockOptionsDropDown: Function;
}) => {
  const dropDownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const toolbar = props.toolbarRef.current;
    const dropDown = dropDownRef.current;

    if (toolbar !== null && dropDown !== null) {
      const { top, left } = toolbar.getBoundingClientRect();
      dropDown.style.top = `${top + 40}px`;
      dropDown.style.left = `${left}px`;
    }
  }, [dropDownRef, props.toolbarRef]);

  useEffect(() => {
    const dropDown = dropDownRef.current;
    const toolbar = props.toolbarRef.current;

    if (dropDown !== null && toolbar !== null) {
      const handle = (event: MouseEvent) => {
        const target = event.target;
        if (
          !(target instanceof Element) ||
          (!dropDown.contains(target) && !toolbar.contains(target))
        ) {
          props.setShowBlockOptionsDropDown(false);
        }
      };
      document.addEventListener("click", handle);

      return () => {
        document.removeEventListener("click", handle);
      };
    }
    // we don't need to run this useEffect when the ShowBlockOptionsDropDown function changes, and lint is complaining
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropDownRef, props.toolbarRef]);

  const formatParagraph = () => {
    if (props.blockType !== "paragraph") {
      props.editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createParagraphNode());
        }
      });
    }
    props.setShowBlockOptionsDropDown(false);
  };

  const formatLargeHeading = () => {
    if (props.blockType !== "h1") {
      props.editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode("h1"));
        }
      });
    }
    props.setShowBlockOptionsDropDown(false);
  };

  const formatSmallHeading = () => {
    if (props.blockType !== "h2") {
      props.editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode("h2"));
        }
      });
    }
    props.setShowBlockOptionsDropDown(false);
  };

  const formatBulletList = () => {
    if (props.blockType !== "ul") {
      props.editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      props.editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
    props.setShowBlockOptionsDropDown(false);
  };

  const formatNumberedList = () => {
    if (props.blockType !== "ol") {
      props.editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      props.editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
    props.setShowBlockOptionsDropDown(false);
  };

  const formatQuote = () => {
    if (props.blockType !== "quote") {
      props.editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createQuoteNode());
        }
      });
    }
    props.setShowBlockOptionsDropDown(false);
  };

  return (
    <div className="dropdown" ref={dropDownRef}>
      <button type="button" className={props.blockType === "paragraph" ? "item active-item" : "item"} onClick={formatParagraph}>
        <span className="text">Normal</span>
      </button>
      <button type="button" className={props.blockType === "h1" ? "item active-item" : "item"} onClick={formatLargeHeading}>
        <span className="text">Large Heading</span>
      </button>
      <button type="button" className={props.blockType === "h2" ? "item active-item" : "item"} onClick={formatSmallHeading}>
        <span className="text">Small Heading</span>
      </button>
      <button type="button" className={props.blockType === "ul" ? "item active-item" : "item"} onClick={formatBulletList}>
        <span className="text">Bullet List</span>
      </button>
      <button type="button" className={props.blockType === "ol" ? "item active-item" : "item"} onClick={formatNumberedList}>
        <span className="text">Numbered List</span>
      </button>
      <button type="button" className={props.blockType === "quote" ? "item active-item" : "item"} onClick={formatQuote}>
        <span className="text">Quote</span>
      </button>
    </div>
  );
};

const TableDropdown = (props: {
  editor: LexicalEditor;
  toolbarRef: RefObject<HTMLDivElement>;
  setShowTableOptions: Function;
}) => {
  const [rows, setRows] = useState("5");
  const [columns, setColumns] = useState("5");
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const toolbar = props.toolbarRef.current;
    const dropDown = optionsRef.current;

    if (toolbar !== null && dropDown !== null) {
      const { top, left } = toolbar.getBoundingClientRect();
      dropDown.style.top = `${top + 40}px`;
      dropDown.style.left = `${left}px`;
    }
  }, [optionsRef, props.toolbarRef]);

  const onClick = () => {
    props.editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns, rows });
    props.setShowTableOptions(false);
  };

  return (
    <div className="dropdown" ref={optionsRef}>
      <TextInput label="No of rows" onChange={setRows} value={rows} />
      <TextInput label="No of columns" onChange={setColumns} value={columns} />
      <div className="dialog-buttons">
        <button
          type="button"
          className="email-style-button"
          onClick={() => props.setShowTableOptions(false)}
        >
          Cancel
        </button>
        <button type="button" className="email-style-button" onClick={onClick}>
          Confirm
        </button>
      </div>
    </div>
  );
};

export default function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState("paragraph");
  const [showBlockOptionsDropDown, setShowBlockOptionsDropDown] =
    useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [fontColor, setFontColor] = useState<string>("#000");
  const [bgColor, setBgColor] = useState<string>("#fff");
  const [showTableOptions, setShowTableOptions] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          setBlockType(type);
        }
      }
      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      // Handle Buttons
      setFontColor(
        $getSelectionStyleValueForProperty(selection, "color", "#000")
      );
      setBgColor(
        $getSelectionStyleValueForProperty(
          selection,
          "background-color",
          "#fff"
        )
      );
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const applyStyleText = useCallback(
    (styles: Record<string, string>) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, styles);
        }
      });
    },
    [editor]
  );

  const onFontColorSelect = useCallback(
    (value: string) => {
      applyStyleText({ color: value });
    },
    [applyStyleText]
  );

  const onBgColorSelect = useCallback(
    (value: string) => {
      applyStyleText({ "background-color": value });
    },
    [applyStyleText]
  );

  return (
    <div className="toolbar" ref={toolbarRef}>
      <button
        type="button"
        disabled={!canUndo}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        className="toolbar-item spaced"
        aria-label="Undo"
      >
        <Undo />
      </button>
      <button
        type="button"
        disabled={!canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        className="toolbar-item"
        aria-label="Redo"
      >
        <Redo />
      </button>
      <Divider />
      {supportedBlockTypes.has(blockType) && (
        <>
          <button
            type="button"
            className="toolbar-item block-controls"
            onClick={() =>
              setShowBlockOptionsDropDown(!showBlockOptionsDropDown)
            }
            aria-label="Formatting Options"
          >
            <span className="text">
              {blockTypeToBlockName[blockType as keyof BlockTypeNameMap]}
            </span>
            <ExpandMoreRoundedIcon />
          </button>
          {showBlockOptionsDropDown &&
            createPortal(
              <BlockOptionsDropdownList
                editor={editor}
                blockType={blockType}
                toolbarRef={toolbarRef}
                setShowBlockOptionsDropDown={setShowBlockOptionsDropDown}
              />,
              // very hacky way to handle this, but not sure how else to deal with it atm.
              document.getElementById("email-modal") || document.body
            )}
          <Divider />
        </>
      )}
      <>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
          }}
          className={"toolbar-item spaced " + (isBold ? "active" : "")}
          aria-label="Format Bold"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
          }}
          className={"toolbar-item spaced " + (isItalic ? "active" : "")}
          aria-label="Format Italics"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
          }}
          className={"toolbar-item spaced " + (isUnderline ? "active" : "")}
          aria-label="Format Underline"
        >
          U
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
          }}
          className={"toolbar-item spaced " + (isStrikethrough ? "active" : "")}
          aria-label="Format Strikethrough"
        >
          S
        </button>
        <button
          type="button"
          onClick={insertLink}
          className={"toolbar-item spaced " + (isLink ? "active" : "")}
          aria-label="Insert Link"
        >
          <AddLink />
        </button>
        <ColorPicker
          buttonClassName="toolbar-item color-picker"
          buttonAriaLabel="Formatting text color"
          buttonIconClassName="icon font-color"
          color={fontColor}
          onChange={onFontColorSelect}
          title="text color"
        />
        <ColorPicker
          buttonClassName="toolbar-item color-picker"
          buttonAriaLabel="Formatting background color"
          buttonIconClassName="icon bg-color"
          color={bgColor}
          onChange={onBgColorSelect}
          title="bg color"
        />
        {isLink &&
          createPortal(
            <FloatingLinkEditor editor={editor} />,
            document.getElementById("email-modal") || document.body
          )}
        <Divider />
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
          }}
          className="toolbar-item spaced"
          aria-label="Left Align"
        >
          <FormatAlignLeft />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
          }}
          className="toolbar-item spaced"
          aria-label="Center Align"
        >
          <FormatAlignCenter />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
          }}
          className="toolbar-item spaced"
          aria-label="Right Align"
        >
          <FormatAlignRight />
        </button>
        <button
          type="button"
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify");
          }}
          className="toolbar-item spaced"
          aria-label="Justify Align"
        >
          <FormatAlignJustify />
        </button>
        <button
          type="button"
          onClick={() => {
            setShowTableOptions(!showTableOptions);
          }}
          className="toolbar-item"
          aria-label="Insert Table"
        >
          Table {/* TODO: update */}
        </button>
        {showTableOptions &&
          createPortal(
            <TableDropdown
              editor={editor}
              toolbarRef={toolbarRef}
              setShowTableOptions={setShowTableOptions}
            />,
            // very hacky way to handle this, but not sure how else to deal with it atm.
            document.getElementById("email-modal") || document.body
          )}
      </>
    </div>
  );
}
